import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, ExternalLink, MessageCircle, Quote, ShieldCheck, ShoppingCart } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useSite } from "../lib/site";
import Lightbox from "../components/Lightbox";
import Skeleton from "../components/Skeleton";

export default function BookDetail() {
  const { slug } = useParams();
  const { settings } = useSite();
  const [state, setState] = useState({ loading: true, book: null, others: [] });
  const [imgIndex, setImgIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useSeo({
    title: state.book ? `${state.book.title} — Heri Sensei` : "Karya — Heri Sensei",
    description: state.book?.subtitle || "Katalog buku dan karya Heri Sensei.",
    path: `/buku-karya/${slug}`,
  });

  useEffect(() => {
    setState({ loading: true, book: null, others: [] });
    setImgIndex(0);
    api.get(`/books/by-slug/${slug}`)
      .then((r) => {
        if (r.data.redirect) {
          setState({ loading: false, redirect: r.data.redirect });
          return;
        }
        setState({ loading: false, book: r.data.book, others: r.data.others });
      })
      .catch(() => setState({ loading: false, book: null }));
  }, [slug]);

  if (state.loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-24 grid grid-cols-1 md:grid-cols-2 gap-10" aria-busy="true">
        <Skeleton className="h-[420px]" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-40" />
        </div>
      </div>
    );
  }

  const b = state.book;
  if (!b) {
    return (
      <div className="max-w-[720px] mx-auto px-4 sm:px-8 py-24 text-center" data-testid="book-not-found">
        <span className="w-2.5 h-2.5 rounded-full bg-enji inline-block mb-6" />
        <h1 className="font-serif text-3xl text-ink">Karya tidak ditemukan</h1>
        <div className="flex gap-3 justify-center mt-8">
          <Link to="/buku-karya" className="btn-primary">Lihat semua karya</Link>
          <Link to="/" className="btn-ghost">Beranda</Link>
        </div>
      </div>
    );
  }

  const gallery = [b.cover_id, ...(b.gallery || [])].filter(Boolean).map((id) => ({
    id,
    src: mediaUrl(id),
    alt: `Visual ${b.title}`,
  }));

  const BuyButton = ({ big = false }) =>
    b.checkout_url ? (
      <a
        href={b.checkout_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={big ? "book-buy-button" : "book-final-buy-button"}
        className={`btn-primary ${big ? "!min-h-[52px] !px-10 text-base" : ""}`}
      >
        <ShoppingCart size={17} /> {b.cta_label || "Beli di Lynk.id"}
      </a>
    ) : (
      <span data-testid="book-buy-disabled" className="block">
        <button className={`btn-primary ${big ? "!min-h-[52px] !px-10 text-base" : ""} w-full sm:w-auto`} disabled aria-disabled="true">
          <ShoppingCart size={17} /> {b.cta_label || "Beli di Lynk.id"}
        </button>
        <span className="block text-[13px] text-faint mt-3 leading-relaxed">
          Tautan pembelian belum aktif. Hubungi pemilik melalui <Link to="/kontak" className="underline">halaman kontak</Link>.
        </span>
      </span>
    );

  return (
    <div data-testid="book-detail-page" className="py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <Link to="/buku-karya" className="inline-flex items-center gap-2 text-sm text-sub hover:text-ink" data-testid="back-to-books">&larr; Semua karya</Link>

        {/* HERO */}
        <section className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14 mt-8 items-center">
          <div className="md:col-span-5 order-1" data-testid="book-gallery">
            <div className="relative">
              <div className="rounded-xl border border-line bg-surface-muted overflow-hidden" style={{ aspectRatio: "4/5" }}>
                {gallery.length > 0 ? (
                  <img
                    src={gallery[imgIndex].src}
                    alt={gallery[imgIndex].alt}
                    data-testid="book-gallery-main"
                    className="w-full h-full object-contain cursor-zoom-in"
                    onClick={() => setLightbox(imgIndex)}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                    <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-faint">{b.type}</span>
                    <span className="font-serif text-2xl text-sub mt-4">{b.title}</span>
                  </div>
                )}
              </div>
              <span className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-enji" aria-hidden="true" />
            </div>
            {gallery.length > 1 && (
              <>
                <div className="flex gap-3 mt-4 overflow-x-auto no-scrollbar" role="group" aria-label="Pilih gambar">
                  {gallery.map((g, i) => (
                    <button
                      key={g.id}
                      data-testid={`book-gallery-thumb-${i}`}
                      onClick={() => setImgIndex(i)}
                      aria-label={`Gambar ${i + 1}`}
                      className={`shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${i === imgIndex ? "border-enji" : "border-line"}`}
                    >
                      <img src={g.src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <div className="flex gap-3 mt-3">
                  <button onClick={() => setImgIndex((imgIndex - 1 + gallery.length) % gallery.length)} data-testid="gallery-prev" className="btn-ghost !min-h-[40px] !px-4 text-sm flex-1">Sebelumnya</button>
                  <button onClick={() => setImgIndex((imgIndex + 1) % gallery.length)} data-testid="gallery-next" className="btn-ghost !min-h-[40px] !px-4 text-sm flex-1">Berikutnya</button>
                </div>
              </>
            )}
          </div>

          <div className="md:col-span-7 order-2">
            <span className="inline-block px-3.5 py-1.5 rounded-full bg-enji/10 text-enji text-[12px] uppercase tracking-[0.14em] font-semibold" data-testid="book-type-label">
              {b.type}
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] tracking-tight leading-tight mt-5 text-ink" data-testid="book-title">{b.title}</h1>
            {b.subtitle && <p className="text-sub text-lg mt-3 leading-relaxed">{b.subtitle}</p>}

            {(b.benefits || []).length > 0 && (
              <ul className="mt-7 space-y-3" data-testid="book-benefits">
                {b.benefits.map((ben, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-ink">
                    <span className="w-6 h-6 rounded-full bg-moss/15 text-moss flex items-center justify-center shrink-0 mt-0.5"><Check size={13} /></span>
                    {ben}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-5" data-testid="book-buy-panel">
              {b.price && (
                <p className="font-serif text-2xl text-ink" data-testid="book-price">
                  {b.price}
                  {b.price_note && <span className="block font-sans text-[13px] font-normal text-faint mt-1">{b.price_note}</span>}
                </p>
              )}
              <BuyButton big />
            </div>

            {b.guarantee_note && (
              <p className="mt-5 flex items-start gap-2.5 text-[13px] text-sub leading-relaxed max-w-md" data-testid="book-guarantee">
                <ShieldCheck size={16} className="text-moss shrink-0 mt-0.5" />
                {b.guarantee_note}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2 text-[12px]">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-line bg-surface text-sub">
                <ShieldCheck size={12} className="text-enji" /> Checkout aman via Lynk.id
              </span>
              <Link to="/kontak" data-testid="book-ask-button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-line bg-surface text-sub hover:text-enji hover:border-enji transition-colors">
                <MessageCircle size={12} /> Tanya dulu sebelum membeli
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* UNTUK SIAPA */}
      {b.audience && (
        <section className="mt-16 bg-surface-muted/70 border-y border-line" data-testid="book-audience">
          <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 text-center">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">Untuk Siapa Karya Ini</p>
            <p className="font-serif text-2xl md:text-[1.55rem] leading-relaxed text-ink mt-4">{b.audience}</p>
          </div>
        </section>
      )}

      {/* TENTANG & ISI */}
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        {(b.description || b.contents) && (
          <section className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-10">
            {b.description && (
              <div data-testid="book-description">
                <h2 className="font-serif text-3xl tracking-tight text-ink mb-6">Tentang Karya Ini</h2>
                <div className="reader" dangerouslySetInnerHTML={{ __html: b.description }} />
              </div>
            )}
            {b.contents && (
              <div data-testid="book-contents">
                <h2 className="font-serif text-3xl tracking-tight text-ink mb-6">Isi / Daftar Bab</h2>
                <div className="reader" dangerouslySetInnerHTML={{ __html: b.contents }} />
              </div>
            )}
          </section>
        )}

        {gallery.length > 1 && (
          <section className="mt-16" data-testid="book-gallery-grid">
            <h2 className="font-serif text-3xl tracking-tight text-ink mb-8">Tampilan Lebih Dekat</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {gallery.slice(1).map((g, i) => (
                <button key={g.id} onClick={() => setLightbox(i + 1)} className="rounded-lg overflow-hidden border border-line bg-surface-muted" style={{ aspectRatio: "4/5" }} data-testid={`book-gallery-item-${i}`}>
                  <img src={g.src} alt={g.alt} loading="lazy" className="w-full h-full object-cover hover:scale-[1.02] transition-transform" />
                </button>
              ))}
            </div>
          </section>
        )}

        {b.sample_note && (
          <section className="mt-14 max-w-[760px] border border-dashed border-line rounded-xl p-7 text-sm text-sub leading-relaxed" data-testid="book-sample-note">
            {b.sample_note}
          </section>
        )}

        {(b.testimonials || []).length > 0 && (
          <section className="mt-16" data-testid="book-testimonials">
            <h2 className="font-serif text-3xl tracking-tight text-ink mb-8">Kata Pembaca</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {b.testimonials.map((t, i) => (
                <figure key={i} data-testid={`book-testimonial-${i}`} className="bg-surface border border-line rounded-xl p-6">
                  <Quote size={18} className="text-enji" />
                  <blockquote className="text-[15px] leading-relaxed text-ink mt-3">{t.text}</blockquote>
                  <figcaption className="text-[13px] text-faint mt-4">— {t.name}{t.role ? `, ${t.role}` : ""}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {(b.faq || []).length > 0 && (
          <section className="mt-16 max-w-[760px]" data-testid="book-faq">
            <h2 className="font-serif text-3xl tracking-tight text-ink mb-6">Pertanyaan Umum</h2>
            <div className="space-y-3">
              {b.faq.map((f, i) => (
                <details key={i} className="group bg-surface border border-line rounded-lg">
                  <summary className="flex items-center justify-between cursor-pointer list-none p-5 font-semibold text-[15px] text-ink">
                    {f.q}
                    <span className="text-enji group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="px-5 pb-5 text-sub text-[15px] leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* TRUST PENULIS */}
        <section className="mt-16 bg-surface border border-line rounded-xl p-7 sm:p-8 flex flex-col sm:flex-row items-start gap-6" data-testid="book-author-trust">
          {settings.profile_photo_id ? (
            <img src={mediaUrl(settings.profile_photo_id)} alt={settings.profile_photo_alt || `Potret ${settings.full_name || "penulis"}`} className="w-20 h-20 rounded-full object-cover border border-line shrink-0" />
          ) : (
            <span className="w-20 h-20 rounded-full bg-enji/10 text-enji font-serif text-2xl flex items-center justify-center shrink-0">H</span>
          )}
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">Tentang Penulis</p>
            <p className="font-serif text-2xl text-ink mt-2">{settings.full_name || "Heri Ilham, S.S., M.M."}</p>
            <p className="text-sub text-[15px] leading-relaxed mt-2 max-w-xl">{settings.tagline || "Pendidik dan penulis."}</p>
            <Link to="/tentang" className="link-underline text-sm font-semibold text-enji mt-3 inline-block">Kenali saya lebih dekat</Link>
          </div>
        </section>

        {/* CTA AKHIR */}
        <section className="mt-16 rounded-xl bg-indigo text-center px-6 sm:px-12 py-14" data-testid="book-final-cta">
          <span className="w-2.5 h-2.5 rounded-full bg-enji inline-block mb-6" aria-hidden="true" />
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#F7F5F0]">Siap membawa pulang karya ini?</h2>
          {b.subtitle && <p className="text-[#B8C2C9] mt-4 max-w-lg mx-auto leading-relaxed">{b.subtitle}</p>}
          <div className="mt-8 flex justify-center">
            <span className="[&>span]:!block [&_a]:!min-h-[52px] [&_a]:!px-10 [&_a]:!text-base [&_button]:!w-auto">
              <BuyButton />
            </span>
          </div>
          {b.guarantee_note && <p className="text-[13px] text-[#8FA0AA] mt-6 max-w-md mx-auto">{b.guarantee_note}</p>}
        </section>

        {state.others.length > 0 && (
          <section className="mt-20 pb-4">
            <h2 className="font-serif text-3xl tracking-tight text-ink mb-8">Karya Lainnya</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {state.others.slice(0, 4).map((o, i) => (
                <Link key={o.id} to={`/buku-karya/${o.slug}`} data-testid={`book-other-${o.slug}`} className="group bg-surface border border-line rounded-lg overflow-hidden hover:shadow-[0_12px_32px_rgba(32,37,34,0.08)] transition-shadow">
                  <div className="bg-surface-muted relative" style={{ aspectRatio: "3/4" }}>
                    {o.cover_id ? (
                      <img src={mediaUrl(o.cover_id)} alt={`Cover ${o.title}`} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center p-4 text-center">
                        <span className="font-serif text-lg text-sub">{o.title}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-enji">{o.type}</span>
                    <p className="font-serif text-[15px] leading-snug mt-1.5 text-ink">{o.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {b.checkout_url && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-ivory/95 backdrop-blur border-t border-line px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom))] flex items-center gap-4" data-testid="book-sticky-buy-bar">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink truncate">{b.title}</p>
            {b.price && <p className="text-[13px] text-sub">{b.price}</p>}
          </div>
          <a href={b.checkout_url} target="_blank" rel="noopener noreferrer" data-testid="book-sticky-buy-button" className="btn-primary !min-h-[44px] shrink-0 ml-auto">
            Beli <ExternalLink size={14} />
          </a>
        </div>
      )}

      <Lightbox
        images={gallery}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onPrev={gallery.length > 1 ? () => setLightbox((lightbox - 1 + gallery.length) % gallery.length) : null}
        onNext={gallery.length > 1 ? () => setLightbox((lightbox + 1) % gallery.length) : null}
      />
    </div>
  );
}
